import type {
	CreateProjectDirectThreadRequest,
	MarkProjectDirectThreadReadRequest,
	ProjectDirectMessagePageResponse,
	ProjectDirectThreadDto,
	SendProjectDirectMessageRequest,
} from "@shared/types";
import type { TokenRequest } from "ably";
import { apiClient } from "@/lib/api-client";

const API_BASE = "/api/v1/projects";

export interface ProjectDirectMessagesRealtimeAuthResponse {
	tokenRequest: TokenRequest;
	channel: string;
	clientId: string;
}

export async function getProjectDirectThreads(
	projectId: string,
): Promise<ProjectDirectThreadDto[]> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/${projectId}/direct-messages/threads`,
		);
		return response.data.data.threads;
	} catch {
		return [];
	}
}

export async function createOrGetProjectDirectThread(
	projectId: string,
	payload: CreateProjectDirectThreadRequest,
): Promise<ProjectDirectThreadDto> {
	const response = await apiClient.post(
		`${API_BASE}/${projectId}/direct-messages/threads`,
		payload,
	);
	return response.data.data.thread;
}

export async function getProjectDirectMessages(
	projectId: string,
	threadId: string,
	params: { cursor?: string; limit?: number } = {},
): Promise<ProjectDirectMessagePageResponse> {
	const response = await apiClient.get(
		`${API_BASE}/${projectId}/direct-messages/threads/${threadId}/messages`,
		{ params },
	);
	return response.data.data;
}

export async function sendProjectDirectMessage(
	projectId: string,
	threadId: string,
	payload: SendProjectDirectMessageRequest,
): Promise<{
	success: boolean;
	message?: ProjectDirectMessagePageResponse["messages"][number];
	error?: string;
}> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/${projectId}/direct-messages/threads/${threadId}/messages`,
			payload,
		);
		return { success: true, message: response.data.data.message };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to send direct message",
		};
	}
}

export async function markProjectDirectThreadRead(
	projectId: string,
	threadId: string,
	payload: MarkProjectDirectThreadReadRequest,
): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.post(
			`${API_BASE}/${projectId}/direct-messages/threads/${threadId}/read`,
			payload,
		);
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error:
				err.response?.data?.message || "Failed to mark conversation as read",
		};
	}
}

export async function createProjectDirectMessagesRealtimeAuth(
	projectId: string,
): Promise<ProjectDirectMessagesRealtimeAuthResponse> {
	const response = await apiClient.post(
		`${API_BASE}/${projectId}/direct-messages/realtime/auth`,
	);
	return response.data.data;
}
